//@ts-nocheck
import { Editor, Transforms, Node, Path, Element } from 'slate'
import { generateId } from '../../../utils/hooks/withId'
import { isSelectionCollapsed } from '../../../utils/queries'
import styles from './style.module.css'
import { isEqual } from 'lodash';

export const LIST_TYPES = ['ol', 'ul'] as const;

export const getRootListPath = (editor, path): any => {
  const rootList = Editor.above(editor, {
    at: path,
    match(n, p) {
      return (
        LIST_TYPES.includes(n?.["type"]) &&
        !LIST_TYPES.includes(Editor.parent(editor, p)?.[0]?.["type"]) &&
        Editor.previous(editor, { at: p })?.[0]?.["type"] !== 'li'
      );

    },
  })

  return rootList ? rootList[1] : null
}

const comparePaths = (pathA, pathB) => {
  const lenA = pathA.length;
  const lenB = pathB.length;
  const minLen = Math.min(lenA, lenB);

  for (let i = 0; i < minLen; i++) {
    if (pathA[i] < pathB[i]) return -1; // pathA is before pathB
    if (pathA[i] > pathB[i]) return 1;  // pathA is after pathB
  }

  // If one path is a prefix of the other, the shorter one comes first
  if (lenA < lenB) return -1;
  if (lenA > lenB) return 1;

  return 0; // Paths are equal
};
const isPathWithinPaths = (nodePath: Path, startPath: Path, endPath: Path): boolean => {
  if (Path.compare(startPath, endPath) > 0) {
    [startPath, endPath] = [endPath, startPath]; // Swap if needed
  }
  return comparePaths(nodePath, startPath) >= 0 && comparePaths(nodePath, endPath) <= 0
};
const getParentNode = (editor, nodePath, nodeType: string | string[]) => {
  const [parentLi] = Editor.nodes(editor, {
    at: nodePath,
    mode: 'lowest',
    match: (node, path) => {
      if (Array.isArray(nodeType)) return Element.isElement(node) && nodeType.includes(node['type']) && Path.isAncestor(path, nodePath);
      return Element.isElement(node) && node['type'] === nodeType && Path.isAncestor(path, nodePath);
    }
  })
  return parentLi
}
const findStartEndPaths = (editor, nodeType: string | string[]): [Path, Path] => {
  let anchorPath = editor.selection.anchor.path;
  let focusPath = editor.selection.focus.path;
  if (Path.compare(anchorPath, focusPath) > 0) {
    [anchorPath, focusPath] = [focusPath, anchorPath]; // Swap if needed
  }
  const startNodeEntry = getParentNode(editor, anchorPath, nodeType)
  const endNodeEntry = getParentNode(editor, focusPath, nodeType)
  const startPath = startNodeEntry ? startNodeEntry[1] : anchorPath;
  const endPath = endNodeEntry ? endNodeEntry[1] : focusPath;
  return [startPath, endPath]
}

const removeIFOnlyOneFragment = (editor, path) => {
  const node = Node.get(editor, path);
  if (node.children.length === 1 && node.children[0].type === 'fragment') {
    // remove the fragment wrapper
    Transforms.unwrapNodes(editor, { at: path.concat(0) });
  }
}
const findListByLiPaths = (editor, liPaths) => {
  // group li by length
  const groupedLiPaths = liPaths.reduce((acc, path) => {
    const key = path.length;
    if (!acc[key]) acc[key] = [];
    acc[key].push(path);
    return acc;
  }, {});
  const listPaths = [];
  // find listPath by one of li from each group
  for (const key in groupedLiPaths) {
    const liPath = groupedLiPaths[key][0];
    const listNode = getParentNode(editor, liPath, ['ol', 'ul']);
    if (listNode) {
      listPaths.push(listNode);
    }
  }
  return listPaths;
}
const findNestedNodeInChildren = (children, parentPath, matchNodeType) => {
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const childPath = [...parentPath, i];

    if (Element.isElement(child) && matchNodeType.includes(child.type)) {
      return { node: child, path: childPath }; // Found first nested list
    }

    if (Element.isElement(child) && child.children) {
      const result = findNestedNodeInChildren(child.children, childPath, matchNodeType);
      if (result) return result; // Return the first found list
    }
  }
  return null;
};
const findSiblingLiTillRootList = (editor, liPath) => {
  let children = [];
  let listType = null;

  const getParentNode = (editor, nodePath, nodeType) => {
    return Editor.nodes(editor, {
      at: nodePath,
      mode: 'lowest',
      match: (node, path) => {
        if (Array.isArray(nodeType)) return Element.isElement(node) && nodeType.includes(node['type']) && Path.isAncestor(path, nodePath);
        return Element.isElement(node) && node['type'] === nodeType && Path.isAncestor(path, nodePath);
      }
    })
  }

  const filterLi = (editor, liPath, children = []) => {
    const listNode = Node.parent(editor, liPath);
    const listItemIndex = liPath[liPath.length - 1];

    const moveLi = []

    const listPath = Path.parent(liPath);
    for (let index = listNode.children.length - 1; index > listItemIndex; index--) {
      const child = listNode.children[index];
      if (child.type === 'li') {
        if (!listType) listType = listNode.type;
        moveLi.unshift(child);
        Transforms.removeNodes(editor, { at: listPath.concat(index) });
      }
    }
    moveLi.forEach((li) => { children.push(li) });

    const allLi = Array.from(getParentNode(editor, listPath, 'li'));
    if (allLi.length > 0) {
      const [, parentLiPath] = allLi[0];
      filterLi(editor, parentLiPath, children);
    }
  }
  filterLi(editor, liPath, children);
  return { children, listType };
}
const removeListFromNested = (editor, liPath) => {
  let result = { children: [], listType: null };
  // return if the path is not a list
  const liNode = Node.get(editor, liPath);
  // if any list is present inside the li then push list after the paragraph    
  const nestedList = findNestedNodeInChildren(liNode.children, liPath, ["ul", "ol"]);
  if (nestedList) {
    result.children = nestedList.node.children;
    result.listType = nestedList.node.type;
    Transforms.removeNodes(editor, { at: nestedList.path });
  }
  // if sibling li present after selected then move it to the next path below of the paragraph with start of remaining items
  return result;
}
const convertListToParagraphs = (editor, liPaths, isMultiple) => {
  if (!editor || !liPaths) return;
  let lastLiPath = liPaths
  if (isMultiple) {
    lastLiPath = liPaths[liPaths.length - 1];
  }

  Editor.withoutNormalizing(editor, () => {

    // find all li after the selected li and remove from main list and save top li into array
    let nextList = { children: [], listType: null };
    nextList = findSiblingLiTillRootList(editor, lastLiPath);

    // if any list is present inside the li then push list after the paragraph   
    const insideList = removeListFromNested(editor, lastLiPath);
    if (insideList.children.length > 0) {
      nextList.children = [...insideList.children, ...nextList.children];
      nextList.listType = insideList.listType || nextList.listType;
    }

    // find path where need to add li as a paragraph
    let nextPath = lastLiPath.slice(0, 2);
    if (!isMultiple) {
      // Convert "li" into a paragraph (`p`)
      Transforms.setNodes(editor, { type: "p" }, { at: lastLiPath });
      // move li from the list and add it to start level as a paragraph    
      nextPath = Path.next(nextPath);
      Transforms.moveNodes(editor, {
        at: lastLiPath,
        to: nextPath, // Move it after the original list
      });
      removeIFOnlyOneFragment(editor, nextPath);
    } else {
      // get all li in reverse order and move it to the next path below of the paragraph as paragraph
      liPaths.reverse().forEach((currentLiPath) => {
        Transforms.setNodes(editor, { type: "p" }, { at: currentLiPath });
        const currentLiNode = Node.get(editor, currentLiPath);
        const nestedList = findNestedNodeInChildren(currentLiNode.children, currentLiPath, ["ul", "ol"]);
        if (nestedList) Transforms.removeNodes(editor, { at: nestedList.path });
        const movePath = Path.next(nextPath)
        Transforms.moveNodes(editor, { at: currentLiPath, to: movePath });
        removeIFOnlyOneFragment(editor, movePath);
      })
      // update path as per selected Li
      nextPath[nextPath.length - 1] += liPaths.length;
    }

    // push nested and sibling item under new list to the next path as new List
    if (nextList.children.length > 0) {
      // Step 5: Insert a new list **after** the paragraph for the remaining items
      nextPath = Path.next(nextPath);
      Transforms.insertNodes(
        editor,
        {
          type: nextList.listType, // Maintain original list type (ul/ol)
          children: nextList.children,
        },
        { at: nextPath } // Place it after the paragraph
      );
    }
  })

}

export const listWrapperElement = (format) => {
  return { type: format, children: [], id: generateId() }
}

export const changeFormatIfParentIsFragment = (editor, format, currentElement, currentElementPath) => {
  if (currentElement.type === 'fragment') {
    const [parentElement, parentElementPath] = Editor.parent(editor, currentElementPath)
    if (parentElement.type === 'li') {
      const listType = Editor.parent(editor, parentElementPath)

      // different list then convert
      if (listType?.[0]?.type !== format) {
        Transforms.setNodes(editor, { type: format }, { at: listType[1] })
      } else convertListToParagraphs(editor, parentElementPath, false);
      return true
    }
  }
  return false
}

export const toggleList = (event, editor, format) => {
  event?.preventDefault()
  const selection = editor.selection || editor.savedSeleciton
  if (selection) {
    // incase selection is from savedSelection
    Transforms.select(editor, selection)

    // Remove indentation from nodes being converted to list
    for (const [node, path] of Editor.nodes(editor, {
      at: selection,
      match: (n) => Element.isElement(n) && n.attrs?.['data-indent-level'] !== undefined,
    })) {
      const element = node
      const newAttrs = { ...element.attrs }
      delete newAttrs['data-indent-level']

      Transforms.setNodes(editor, { attrs: newAttrs }, { at: path })
    }

    if (isSelectionCollapsed(selection)) {
      const [currentElement, currentElementPath] = Editor.parent(editor, selection)
      // if it already a list
      if (currentElement.type === 'li') {
        const listType = Editor.parent(editor, currentElementPath)

        // different list then convert
        if (listType?.[0]?.type !== format) {
          Transforms.setNodes(editor, { type: format }, { at: listType[1] })
        }
        return
      }
      if (changeFormatIfParentIsFragment(editor, format, currentElement, currentElementPath)) {
        return;
      }

      // if prevSibling is also a same list
      if (currentElementPath[currentElementPath.length - 1] > 0) {
        const prevSiblingPath = currentElementPath.slice()
        prevSiblingPath[prevSiblingPath.length - 1] -= 1

        const [prevSibling] = Editor.node(editor, prevSiblingPath)
        if (prevSibling.type === format) {
          Editor.withoutNormalizing(editor, () => {
            Transforms.setNodes(editor, {
              type: 'li'
            })

            //@ts-ignore
            const movePosition = [...prevSiblingPath, prevSibling.children.length]
            Transforms.moveNodes(editor, { to: movePosition })
          })
          return
        }
      }
    } else {

      // already a list
      const [startPath, endPath] = findStartEndPaths(editor, 'li')
      const selectedLiNode = Array.from(Editor.nodes(editor, {
        match: (n, path) => n.type === 'li' && isPathWithinPaths(path, startPath, endPath)
      }))
      if (selectedLiNode.length > 0) {

        const [startListPath, endListPath] = findStartEndPaths(editor, ['ol', 'ul']);

        const selectedListNode = findListByLiPaths(editor, selectedLiNode.map(([, liPath]) => liPath));
        const listTypeExists = [];
        // push unique list type inside listTypeExists
        selectedListNode.forEach(([listNode]) => {
          if (!listTypeExists.includes(listNode.type)) listTypeExists.push(listNode.type)
        })

        if (listTypeExists.length === 1 && listTypeExists[0] === format) {
          const selectedLiPaths = selectedLiNode.map(([, liPath]) => liPath)
          convertListToParagraphs(editor, selectedLiPaths, true)
          return;
        } else {
          // if different list then convert
          for (const [listNode, listPath] of selectedListNode) {
            if (listNode.type !== format) {
              Transforms.setNodes(editor, { type: format }, { at: listPath })
            }
          }
          return;
        }
      }
    }

    // create a new list
    Editor.withoutNormalizing(editor, () => {
      Transforms.setNodes(editor, {
        type: 'li'
      })
      const block = listWrapperElement(format)
      Transforms.wrapNodes(editor, block)
    })
  }
}

export const setListTypeAttrs = (props) => {
  const attrs = {};
  if (props?.element?.attrs?.["redactor-attributes"]) {
    attrs['type'] = props?.element?.attrs?.["redactor-attributes"]?.type;
    attrs['className'] = getTypeClassName(props?.element?.attrs?.["redactor-attributes"].type);
  }
  return attrs;
}

export const getTypeClassName = (type) => {
  let styleType = 'type-auto';
  switch (type) {
    case 'a':
      styleType = 'type-lower-alpha';
      break;
    case 'A':
      styleType = 'type-upper-alpha';
      break;
    case 'i':
      styleType = 'type-lower-roman';
      break;
    case 'I':
      styleType = 'type-upper-roman';
      break;
  }
  return styles[styleType];
}

export const isListActive = (editor: Editor, format: typeof LIST_TYPES[number]) => {
  const { selection } = editor
  if (!selection) return false

  const anchorList = Editor.above(editor, {
    match: n => LIST_TYPES.includes(n?.["type"]),
    at: editor.selection?.anchor,
    mode: 'lowest'
  })

  const focusList = Editor.above(editor, {
    match: n => LIST_TYPES.includes(n?.["type"]),
    at: editor.selection?.focus,
    mode: 'lowest'
  })

  return ( focusList && anchorList && focusList[0]?.["type"] === format && anchorList[0]?.["type"] === format && isEqual(focusList[1], anchorList[1]) )
}